import { motion } from "framer-motion";
import { PageHero } from "../components/site/PageHero";
import { Eyebrow, Reveal } from "../components/site/Motion";
import { CTASection } from "../components/site/CTASection";
import { Marquee } from "../components/site/Marquee";
import { usePageMeta } from "../components/site/SmoothScroll";
import { IMAGES } from "../lib/site";

const STEPS = [
  { t: "Listen", d: "We begin with your questions and your story, so the reading speaks to your real situation." },
  { t: "Read", d: "Your birth chart, cards or palm are studied carefully, combining traditional knowledge with intuition." },
  { t: "Guide", d: "You receive honest insight and practical steps — and, where helpful, gentle healing or protective practices." },
];

const VALUES = ["Confidentiality", "Compassion", "Honesty", "Respect", "Clarity", "Patience"];

export default function About() {
  usePageMeta(
    "About Our Astrologer in Calgary | Astrologer & Spiritual Healers",
    "Meet the Calgary astrologer and spiritual healer behind Astrologer & Spiritual Healers — a caring, confidential approach to Vedic astrology, psychic readings and healing."
  );
  return (
    <main data-testid="about-page">
      <PageHero
        testId="about-hero"
        eyebrow="About Us"
        image={IMAGES.meditation}
        lines={[{ text: "A quiet place" }, { text: "for life's louder", className: "italic gold-text" }, { text: "questions" }]}
        intro="Astrologer & Spiritual Healers is a Calgary practice devoted to astrology, intuitive reading and spiritual wellbeing — helping people move through uncertainty with clarity and calm."
      />

      <section className="px-5 py-20 sm:px-8 sm:py-28 lg:px-12">
        <div className="mx-auto grid max-w-[1400px] gap-14 lg:grid-cols-12">
          <Reveal className="lg:col-span-5">
            <div className="clip-notch relative aspect-[4/5] overflow-hidden border border-[#D4AF37]/25">
              <img src="/gods/shiva.webp" alt="Lord Shiva meditating, devotional artwork" loading="lazy" className="h-full w-full object-cover" />
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_30%,transparent_30%,rgba(7,4,13,0.75))]" />
            </div>
          </Reveal>
          <div className="lg:col-span-6 lg:col-start-7">
            <Reveal>
              <Eyebrow>Our Story</Eyebrow>
              <h2 className="font-display mt-5 text-4xl leading-[1.05] text-white sm:text-5xl">Experience guided by <span className="italic gold-text">devotion</span></h2>
            </Reveal>
            <Reveal delay={0.1} className="mt-8 space-y-5 text-base leading-relaxed text-slate-300 sm:text-lg">
              <p>Our astrologer has spent many years studying Vedic astrology, palmistry, tarot and traditional healing practices, learning from teachers and texts that have guided seekers for generations.</p>
              <p>That knowledge is shared with warmth and humility. Whether you are facing a relationship challenge, weighing a career change, hoping for harmony at home or simply feeling unsettled, our role is to help you see your situation more clearly — never to frighten or pressure you.</p>
              <p>We serve clients across Calgary and Alberta, in person at our studio or remotely by phone and WhatsApp.</p>
            </Reveal>
          </div>
        </div>
      </section>

      <section data-testid="about-approach" className="bg-[#0B1120]/60 px-5 py-20 sm:px-8 sm:py-28 lg:px-12">
        <div className="mx-auto max-w-[1400px]">
          <Reveal>
            <Eyebrow>Our Approach</Eyebrow>
            <h2 className="font-display mt-5 max-w-3xl text-4xl leading-[1.05] text-white sm:text-5xl">Three movements in every session</h2>
          </Reveal>
          <div className="mt-14 grid gap-px overflow-hidden border border-[#D4AF37]/15 bg-[#D4AF37]/15 md:grid-cols-3">
            {STEPS.map((s, i) => (
              <motion.div key={s.t} data-testid={`approach-step-${i}`} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.15 }} transition={{ duration: 0.8, delay: i * 0.1 }} className="bg-[#0E081D] p-8 sm:p-10">
                <span className="font-cinzel text-xs tracking-[0.3em] text-[#D4AF37]">STEP 0{i + 1}</span>
                <h3 className="font-display mt-6 text-4xl text-white">{s.t}</h3>
                <p className="mt-4 text-[15px] leading-relaxed text-slate-300">{s.d}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="px-5 py-20 sm:px-8 sm:py-28 lg:px-12">
        <div className="mx-auto grid max-w-[1400px] gap-10 lg:grid-cols-12">
          <Reveal className="lg:col-span-5">
            <Eyebrow>Our Commitment</Eyebrow>
            <h2 className="font-display mt-5 text-4xl leading-[1.05] text-white sm:text-5xl">You are never <span className="italic gold-text">just a chart</span></h2>
          </Reveal>
          <Reveal delay={0.1} className="text-base leading-relaxed text-slate-300 sm:text-lg lg:col-span-6 lg:col-start-7">
            <p>We are committed to treating every client with dignity and care. Readings are offered as guidance to support your own choices, and we will always be honest about what astrology can and cannot tell you.</p>
            <p className="mt-5">Your personal details and the matters you share remain strictly private.</p>
          </Reveal>
        </div>
      </section>

      <Marquee items={VALUES} testId="about-values-marquee" fast />
      <CTASection />
    </main>
  );
}
